import React, { FunctionComponent, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { addDays, addMinutes, isBefore, isEqual, parse, startOfMinute } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { SelectField } from '../../atoms/formElements/SelectField'
import { ClockIcon } from '../../atoms/icons/ClockIcon'
import { ThemeType } from '../../utils/themes/ThemeProvider'

// Fixed English format for parseable values - must match FLEXIBLE_TIME_FORMAT in ProviderCalendarDateSlotSelectionContent
const PARSEABLE_TIME_FORMAT = 'h:mm a'
const STEP_MINUTES = 15

/**
 * Format a Date to a locale-aware time string for display purposes only.
 * Uses Intl.DateTimeFormat with hour12: true for user-friendly localized display.
 * NOTE: Do NOT use this for values that need to be parsed later - use formatTimeForParsing instead.
 */
const formatTimeForDisplay = (date: Date, locale?: string): string => {
  const resolvedLocale = locale ?? Intl.DateTimeFormat().resolvedOptions().locale
  return new Intl.DateTimeFormat(resolvedLocale, { hour: 'numeric', minute: 'numeric', hour12: true }).format(date)
}

/**
 * Format a Date to a fixed English time string suitable for parsing.
 * Always produces format like "9:00 AM" regardless of locale.
 * @param useUTC - If true, format in UTC timezone (for "fake UTC" values from calendar selection)
 */
export const formatTimeForParsing = (date: Date, useUTC?: boolean): string => {
  const timeZone = useUTC ? 'UTC' : Intl.DateTimeFormat().resolvedOptions().timeZone
  return formatInTimeZone(date, timeZone, PARSEABLE_TIME_FORMAT)
}

export type FlexibleTimeRangeSelectorProps = {
  slotMinTime: string
  slotMaxTime: string
  selectedDate: Date
  selectedStartTime: string
  selectedEndTime: string
  onStartTimeChange: (value: string) => void
  onEndTimeChange: (value: string) => void
}

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['16px'],
}))

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
}))

const FieldContainer = styled.View({
  flex: 1,
})

const generateTimeSlots = (
  startTime: Date,
  endTime: Date,
  stepMinutes: number,
  locale: string,
): { label: string; value: string }[] => {
  const slots: { label: string; value: string }[] = []

  let current = startTime
  while (
    isBefore(startOfMinute(current), startOfMinute(endTime)) ||
    isEqual(startOfMinute(current), startOfMinute(endTime))
  ) {
    slots.push({
      label: formatTimeForDisplay(current, locale),
      value: formatTimeForParsing(current),
    })
    current = addMinutes(current, stepMinutes)
  }
  return slots
}

export const FlexibleTimeRangeSelector: FunctionComponent<FlexibleTimeRangeSelectorProps> = ({
  slotMinTime,
  slotMaxTime,
  selectedDate,
  selectedStartTime,
  selectedEndTime,
  onStartTimeChange,
  onEndTimeChange,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const locale = Intl.DateTimeFormat().resolvedOptions().locale

  const startTimeSlots = useMemo(() => {
    const dayStart = parse(slotMinTime, 'HH:mm:ss', selectedDate)
    // For start times, the last selectable slot should be 15 min before the end
    // For midnight (00:00:00), treat it as end-of-day by adding 1 day
    let dayEnd = parse(slotMaxTime, 'HH:mm:ss', selectedDate)
    if (slotMaxTime === '00:00:00') {
      dayEnd = addDays(dayEnd, 1)
    }
    return generateTimeSlots(dayStart, addMinutes(dayEnd, -STEP_MINUTES), STEP_MINUTES, locale)
  }, [slotMinTime, slotMaxTime, selectedDate, locale])

  const endTimeSlots = useMemo(() => {
    if (!selectedStartTime) return []
    const parsedStart = parse(selectedStartTime, PARSEABLE_TIME_FORMAT, selectedDate)
    const minEndTime = addMinutes(parsedStart, STEP_MINUTES)
    // For midnight (00:00:00), treat it as end-of-day by adding 1 day
    let dayEnd = parse(slotMaxTime, 'HH:mm:ss', selectedDate)
    if (slotMaxTime === '00:00:00') {
      dayEnd = addDays(dayEnd, 1)
    }
    return generateTimeSlots(minEndTime, dayEnd, STEP_MINUTES, locale)
  }, [selectedStartTime, slotMaxTime, selectedDate, locale])

  return (
    <Container>
      <BodyText text={<FormattedMessage defaultMessage='Time' description='Label for time selection section' />} />
      <Row>
        <FieldContainer>
          <SelectField
            accessibilityLabel={formatMessage({
              defaultMessage: 'Start time',
              description: 'Label for start time selector',
            })}
            label={formatMessage({ defaultMessage: 'Start', description: 'Start time field label' })}
            value={selectedStartTime}
            name='flexibleStartTime'
            onChange={(value: string) => {
              onStartTimeChange(value)
              onEndTimeChange('')
            }}
            icon={<ClockIcon size={24} fillColor={colors.iconDefault} />}
            options={startTimeSlots}
            baseInputStyle={{ marginBottom: 0 }}
          />
        </FieldContainer>
        <FieldContainer>
          <SelectField
            accessibilityLabel={formatMessage({
              defaultMessage: 'End time',
              description: 'Label for end time selector',
            })}
            label={formatMessage({ defaultMessage: 'End', description: 'End time field label' })}
            value={selectedEndTime}
            name='flexibleEndTime'
            onChange={onEndTimeChange}
            icon={<ClockIcon size={24} fillColor={colors.iconDefault} />}
            options={endTimeSlots}
            baseInputStyle={{ marginBottom: 0 }}
            disabled={!selectedStartTime}
          />
        </FieldContainer>
      </Row>
    </Container>
  )
}
